import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const MyPlans = () => {
  const [plans, setPlans] = useState({
    activePlans: [],
    completedPlans: [],
    stats: {},
    loading: true,
    error: null
  });

  const [activeTab, setActiveTab] = useState('active');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchPlans();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://builtfuturelife.com/api/my-plans', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPlans({
          activePlans: response.data.activePlans || [],
          completedPlans: response.data.completedPlans || [],
          stats: response.data.stats || {},
          loading: false,
          error: null
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch plans');
      }
      
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans({
        activePlans: [],
        completedPlans: [],
        stats: {},
        loading: false,
        error: error.response?.data?.message || error.message || 'Failed to load plans'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (plans.loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        gap: '20px',
        flexDirection: 'column',
        backgroundColor: '#0a0a0a'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(0, 212, 255, 0.2)',
          borderTop: '4px solid #00d4ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#94a3b8' }}>Loading your investment plans...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (plans.error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px',
        padding: '20px',
        backgroundColor: '#0a0a0a'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '16px 24px',
          color: '#f87171',
          textAlign: 'center'
        }}>
          {plans.error}
        </div>
        <button 
          onClick={fetchPlans}
          style={{
            padding: '12px 24px',
            background: '#00d4ff',
            border: 'none',
            borderRadius: '12px',
            color: '#1a1a2e',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '16px' : '30px',
      maxWidth: '1200px',
      margin: '0 auto',
      paddingBottom: isMobile ? '80px' : '30px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh'
    }}>
      {/* Header - Dark Theme */}
      <div style={{
        textAlign: 'center',
        marginBottom: '32px'
      }}>
        <h1 style={{
          fontSize: isMobile ? '24px' : '32px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #fff, #00d4ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '8px'
        }}>My Investment Plans</h1>
        <p style={{ color: '#94a3b8' }}>Track your active and completed investments</p>
      </div>
      
      {/* Stats Overview - Dark Theme */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: '#1a1a2e',
          borderRadius: '20px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '14px',
            color: '#94a3b8',
            margin: '0 0 8px 0'
          }}>Active Investments</h3>
          <p style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#00d4ff',
            margin: 0
          }}>{plans.stats.totalActiveInvestments || 0}</p>
        </div>
        
        <div style={{
          background: '#1a1a2e',
          borderRadius: '20px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '14px',
            color: '#94a3b8',
            margin: '0 0 8px 0'
          }}>Total Invested</h3>
          <p style={{
            fontSize: '28px',
            fontWeight: '700',
            color: 'white',
            margin: 0
          }}>{formatCurrency(plans.stats.totalInvested)}</p>
        </div>
        
        <div style={{
          background: '#1a1a2e',
          borderRadius: '20px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '14px',
            color: '#94a3b8',
            margin: '0 0 8px 0'
          }}>Total Earned</h3>
          <p style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#00d4ff',
            margin: 0
          }}>{formatCurrency(plans.stats.totalEarned)}</p>
        </div>
        
        <div style={{
          background: '#1a1a2e',
          borderRadius: '20px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '14px',
            color: '#94a3b8',
            margin: '0 0 8px 0'
          }}>Expected Returns</h3>
          <p style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#f59e0b',
            margin: 0
          }}>{formatCurrency(plans.stats.totalExpectedReturns || 0)}</p>
        </div>
      </div>

      {/* Tabs - Dark Theme */}
      <div style={{
        display: 'flex',
        gap: isMobile ? '8px' : '16px',
        borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button 
          onClick={() => setActiveTab('active')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'active' ? '2px solid #00d4ff' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'active' ? '600' : '500',
            color: activeTab === 'active' ? '#00d4ff' : '#94a3b8',
            transition: 'all 0.2s'
          }}
        >
          Active Plans ({plans.activePlans.length})
        </button>
        <button 
          onClick={() => setActiveTab('completed')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'completed' ? '2px solid #00d4ff' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'completed' ? '600' : '500',
            color: activeTab === 'completed' ? '#00d4ff' : '#94a3b8',
            transition: 'all 0.2s'
          }}
        >
          Completed Plans ({plans.completedPlans.length})
        </button>
      </div>

      {/* Plans List - Dark Theme */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        {(activeTab === 'active' ? plans.activePlans : plans.completedPlans).map((plan) => {
          const earnedAmount = plan.earningsSoFar || plan.returns?.earnedSoFar || 0;
          const dailyPercent = plan.dailyIncomePercent || plan.returns?.dailyPercent || 0;
          const dailyAmount = plan.dailyIncomeAmount || plan.returns?.dailyAmount || 0;
          const roi = plan.roi || plan.returns?.roi || '0.00%';
          const totalExpected = plan.totalReturn || plan.returns?.totalExpected || 0;
          
          return (
            <div key={plan.investmentId} style={{
              background: '#1a1a2e',
              borderRadius: '24px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              overflow: 'hidden',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}>
              {/* Header */}
              <div style={{
                padding: '16px 20px',
                background: '#0f0f0f',
                borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: 0,
                  color: '#00d4ff'
                }}>{plan.planName}</h3>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  background: plan.status === 'active' ? 'rgba(0, 212, 255, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                  color: plan.status === 'active' ? '#00d4ff' : '#94a3b8'
                }}>
                  {plan.status === 'active' ? 'Active' : 'Completed'}
                </span>
              </div>
              
              {/* Details */}
              <div style={{ padding: '20px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <span style={{ color: '#94a3b8' }}>Investment Amount:</span>
                    <strong style={{ color: 'white' }}>{formatCurrency(plan.investmentAmount)}</strong>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <span style={{ color: '#94a3b8' }}>Daily Income:</span>
                    <strong style={{ color: '#00d4ff' }}>
                      {dailyPercent}% ({formatCurrency(dailyAmount)})
                    </strong>
                  </div>
                  
                  {plan.returns?.hourlyAmount > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 0',
                      borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                    }}>
                      <span style={{ color: '#94a3b8' }}>Hourly Income:</span>
                      <strong style={{ color: '#00d4ff' }}>
                        {formatCurrency(plan.returns.hourlyAmount)}
                      </strong>
                    </div>
                  )}
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <span style={{ color: '#94a3b8' }}>Purchase Date:</span>
                    <span style={{ color: '#e2e8f0' }}>{formatDate(plan.purchaseDate)}</span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                  }}>
                    <span style={{ color: '#94a3b8' }}>Expires:</span>
                    <span style={{ color: '#e2e8f0' }}>{formatDate(plan.expiresAt)}</span>
                  </div>
                  
                  {plan.status === 'active' && plan.daysRemaining !== undefined && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 0',
                      borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
                    }}>
                      <span style={{ color: '#94a3b8' }}>Days Remaining:</span>
                      <span style={{ color: '#f59e0b', fontWeight: '600' }}>{plan.daysRemaining} days</span>
                    </div>
                  )}
                </div>
                
                {/* Highlighted Earned Section - Updated to Cyan */}
                <div style={{
                  background: 'rgba(0, 212, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                  margin: '16px 0',
                  border: '1px solid rgba(0, 212, 255, 0.3)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <span style={{ color: '#94a3b8' }}>Total Earned So Far:</span>
                    <span style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: '#00d4ff'
                    }}>{formatCurrency(earnedAmount)}</span>
                  </div>
                </div>
                
                {/* Progress Bar for Active Plans - Updated to Cyan */}
                {plan.status === 'active' && plan.progressPercentage !== undefined && (
                  <div style={{ margin: '16px 0' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '12px',
                      color: '#94a3b8',
                      marginBottom: '8px'
                    }}>
                      <span>Progress</span>
                      <span>{plan.progressPercentage.toFixed(1)}%</span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${plan.progressPercentage}%`,
                        height: '100%',
                        background: '#00d4ff',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                )}
                
                {/* Total Expected Return */}
                {totalExpected > 0 && (
                  <div style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginTop: '16px',
                    border: '1px solid rgba(245, 158, 11, 0.3)'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <span style={{ color: '#94a3b8' }}>Total Expected Return:</span>
                      <span style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#f59e0b'
                      }}>{formatCurrency(totalExpected)}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer - Updated to Cyan */}
              <div style={{
                padding: '12px 20px',
                background: '#0f0f0f',
                borderTop: '1px solid rgba(0, 212, 255, 0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                fontSize: '12px'
              }}>
                <div style={{
                  background: '#00d4ff',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  color: '#1a1a2e',
                  fontWeight: '600'
                }}>
                  ROI: {roi}
                </div>
                <div style={{
                  color: '#64748b',
                  fontFamily: 'monospace'
                }}>
                  ID: {plan.investmentId}
                </div>
              </div>
            </div>
          );
        })}

        {/* Empty States - Dark Theme */}
        {activeTab === 'active' && plans.activePlans.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📊</div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '8px'
            }}>No Active Investment Plans</h3>
            <p style={{ color: '#94a3b8', marginBottom: '24px' }}>
              You don't have any active investment plans.
            </p>
            <Link to="/plans" style={{
              display: 'inline-block',
              padding: '12px 32px',
              background: '#00d4ff',
              border: 'none',
              borderRadius: '12px',
              color: '#1a1a2e',
              fontSize: '14px',
              fontWeight: '600',
              textDecoration: 'none',
              cursor: 'pointer'
            }}>
              Browse Plans
            </Link>
          </div>
        )}

        {activeTab === 'completed' && plans.completedPlans.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🏆</div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '8px'
            }}>No Completed Plans</h3>
            <p style={{ color: '#94a3b8' }}>
              You haven't completed any investment plans yet.
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default MyPlans;